import type { StorageKeys } from "@/types/web-storage"

import { webStorage } from "@/lib/web-storage"

// Read by an inline script in _document so an opted-in load can start the JS
// Self-Profiler before any app code runs and hydration, the longest task on
// most pages, is in the trace.

type PerfSetup = {
  // localStorage says this tab wants the slow-things report
  optIn: boolean
  // confirmed by the app once it knows the user is Mindtrip
  // (components/perf-report/perf-report-launcher.tsx)
  report: boolean
  profiler?: JsSelfProfiler
}

// lib.dom has no JS Self-Profiling types yet
export type JsSelfProfiler = {
  stop(): Promise<JsSelfProfilerTrace>
  addEventListener(type: "samplebufferfull", listener: () => void): void
}
export type JsSelfProfilerTrace = {
  resources: string[]
  frames: {
    name: string
    resourceId?: number
    line?: number
    column?: number
  }[]
  stacks: { frameId: number; parentId?: number }[]
  samples: { timestamp: number; stackId?: number }[]
}

declare global {
  interface Window {
    __mtPerf?: PerfSetup
  }
}

const PROFILER_OPTIONS = { sampleInterval: 10, maxBufferSize: 6000 }
export const PERF_REPORT_STORAGE_KEY: StorageKeys = "mt:perf-report"

declare const Profiler:
  | (new (options: typeof PROFILER_OPTIONS) => JsSelfProfiler)
  | undefined

export function startJsProfiler(): JsSelfProfiler | undefined {
  if (typeof Profiler !== "function") return undefined
  try {
    return new Profiler(PROFILER_OPTIONS)
  } catch {
    // the page was served without Document-Policy: js-profiling
    return undefined
  }
}

// The opt-in only arms collection in this tab. Whether anything is recorded,
// shown or symbolicated is decided by the launcher once the session is known,
// and /api/perf-report checks the session again server-side.
export function perfSetupScript() {
  return `(function(){var o=false;try{o=localStorage.getItem(${JSON.stringify(PERF_REPORT_STORAGE_KEY)})==="1"}catch(e){}
var p;if(o&&typeof Profiler==="function"){try{p=new Profiler(${JSON.stringify(PROFILER_OPTIONS)})}catch(e){}}
window.__mtPerf={optIn:o,report:false,profiler:p}})()`
}

export function perfOptIn() {
  return typeof window !== "undefined" && window.__mtPerf?.optIn === true
}

/** Whether a later load would record, which is not the same as whether this
 * one is: the setup script reads the flag once, before anything can change it. */
export function perfReportEnabled() {
  return webStorage.getItem(PERF_REPORT_STORAGE_KEY) === "1"
}

// Set from the debug bar (components/perf-report/perf-report-toggle.tsx),
// which only Mindtrip users can open. Recording has to be armed before the
// page loads -- the profiler starts ahead of hydration and the Redux timing
// middleware is chosen when the store is built -- so both directions reload.
export function setPerfReportEnabled(enabled: boolean) {
  if (enabled) webStorage.setItem(PERF_REPORT_STORAGE_KEY, "1")
  else webStorage.removeItem(PERF_REPORT_STORAGE_KEY)
}

// Called by the launcher once the user is known. A tab that opted in without
// a Mindtrip session forgets the opt-in and stops sampling.
export function resolvePerfReport(isMindtripUser: boolean) {
  const setup = window.__mtPerf
  if (!setup?.optIn) return false
  if (isMindtripUser) {
    setup.report = true
    return true
  }
  setup.report = false
  setup.optIn = false
  void setup.profiler?.stop().catch(() => {})
  setup.profiler = undefined
  webStorage.removeItem(PERF_REPORT_STORAGE_KEY)
  return false
}
